import { DEFAULT_OPTIONS, SEEDS } from "./constants.js";
import { clamp } from "./outcome.js";
import type { RatingOptions, RatingState, SeedLevel } from "./types.js";

/** Internal rating that maps to the bottom of the display scale. */
const DISPLAY_ORIGIN = 1100;
/** Internal rating points per 1.000 of display rating. */
const DISPLAY_STEP = 200;

export const DISPLAY_MIN = 2.0;
export const DISPLAY_MAX = 8.0;

/**
 * Map the internal rating onto the 2.000–8.000 scale players actually see.
 *
 * Three decimals is mostly theatre, but it makes small movements visible, and visible
 * movement is what keeps people logging matches.
 */
export function toDisplayRating(r: number): number {
  return clamp(DISPLAY_MIN + (r - DISPLAY_ORIGIN) / DISPLAY_STEP, DISPLAY_MIN, DISPLAY_MAX);
}

/** Inverse of `toDisplayRating`, for seeding from a known display value. */
export function fromDisplayRating(display: number): number {
  return (display - DISPLAY_MIN) * DISPLAY_STEP + DISPLAY_ORIGIN;
}

/** Format as players see it: `4.237`. */
export function formatRating(r: number): string {
  return toDisplayRating(r).toFixed(3);
}

/**
 * Split a display rating into its integer and fractional parts.
 *
 * The UI renders the fraction smaller and in the accent colour — see
 * docs/design-direction.md §5. Doing the split here keeps that lockup consistent
 * everywhere it appears rather than being re-derived per component.
 */
export function splitRating(r: number): { whole: string; fraction: string } {
  const [whole = "0", fraction = "000"] = formatRating(r).split(".");
  return { whole, fraction: `.${fraction}` };
}

/**
 * Confidence in a rating, as a percentage. Derived from RD, never stored — it is a pure
 * function of the rating state, and storing it would create a second source of truth.
 */
export function reliability(rd: number, options: Partial<RatingOptions> = {}): number {
  const o = { ...DEFAULT_OPTIONS, ...options };
  const span = o.rdCeiling - o.rdFloor;
  return clamp(100 * (1 - (rd - o.rdFloor) / span), 0, 100);
}

/** Provisional ratings are kept off the main leaderboard. */
export function isProvisional(rd: number, options: Partial<RatingOptions> = {}): boolean {
  const o = { ...DEFAULT_OPTIONS, ...options };
  return reliability(rd, o) < o.provisionalThreshold;
}

/** Starting state for a new player, from their self-assessment. */
export function seedRating(level: SeedLevel): RatingState {
  return { ...SEEDS[level] };
}
